import { validateAndGetAccessToken } from '@phx/front-end-auth';

import type { ErrorMessageTypes } from '../components/alerts/errors/error-map';
import { CONSTANTS } from '../constants';
import type { GenericApiResponse } from '../models/generic.api.response';
import { setLocalStorageItem } from '../utils/local-storage/local-storage-operations';

const CONFIRM_PATH = '/credentials/contact-otp/confirm';

/**
 * Confirms a verification code with a direct `fetch` rather than through
 * SsoApiClient. apollo-link-rest resolves a 404 as a `null` result instead of
 * rejecting, so an expired code — which patient-agent reports as Twilio's 404 —
 * never reached an error handler and the member saw the generic error.
 */
export const verifyCodeApi = async (
    phoneNumber: string,
    code: string
): Promise<GenericApiResponse> => {
    const verifyCodeRequest = {
        contactValue: phoneNumber,
        confirmationCode: code,
        source: CONSTANTS.otpVerifySource,
    };
    try {
        const accessToken = validateAndGetAccessToken();
        const response = await fetch(
            `${import.meta.env.VITE_SSO_LOGIN_API_URL}${CONFIRM_PATH}`,
            {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...(accessToken
                        ? { authorization: `Bearer ${accessToken}` }
                        : {}),
                },
                body: JSON.stringify(verifyCodeRequest),
            }
        );

        const body = await readBody(response);

        if (response.ok) {
            if (typeof body === 'string' && body.length > 0) {
                setLocalStorageItem(CONSTANTS.tokenKey, body);
                return { success: true };
            }
            return { success: false, error: '500_GenericError' };
        }

        return {
            success: false,
            error: getErrorMessageTypeForVerifyCodeApi(response.status, body),
        };
    } catch {
        return { success: false, error: '500_GenericError' };
    }
};

/**
 * The token arrives as a JSON string or as plain text depending on content
 * negotiation, and error bodies may be JSON or text. Returns the parsed JSON
 * when there is some, otherwise the raw text.
 */
const readBody = async (response: Response): Promise<unknown> => {
    const text = await response.text();
    try {
        return JSON.parse(text) as unknown;
    } catch {
        return text;
    }
};

/**
 * Twilio Verify answers VerificationCheck with 404 once the verification
 * session is gone, and the server passes Twilio's status through verbatim —
 * so a 404 from anywhere else (a gateway, a routing miss) would otherwise be
 * reported to the member as an expired code. Unrecognised 404s fall through
 * to the generic error, which is the behaviour they already had.
 */
const isExpiredVerification = (body: unknown): boolean => {
    // The server shapes its Twilio errors as `{ request: [message] }`, but a
    // 404 from a gateway or proxy is not obliged to look like anything in
    // particular.
    const messages = (body as { request?: unknown } | null)?.request;

    return (
        Array.isArray(messages) &&
        messages.some(
            (message) =>
                typeof message === 'string' &&
                message.includes('VerificationCheck')
        )
    );
};

const getErrorMessageTypeForVerifyCodeApi = (
    statusCode: number,
    body: unknown
): ErrorMessageTypes => {
    switch (statusCode) {
        case 400:
            return '400_InvalidCode';
        case 404:
            return isExpiredVerification(body)
                ? '404_CodeExpired'
                : '500_GenericError';
        case 429:
            return '429_MaxAttemptReached';
    }
    return '500_GenericError';
};
